import styled from "styled-components";
import { theme } from "../../styles/theme";

export const ViewToggleRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 16px;
`;

// Uma linha só, um flex container só - SwitchLayout, busca, ESTADOS,
// SITUAÇÃO, ADICIONAR e RELATÓRIO são todos filhos diretos daqui (nada de
// sub-divs agrupando "input+filtros" separado de "botões"). Em telas largas
// fica nowrap - só o ContainerInput (o único item com flex-grow/shrink) cresce
// ou encolhe pra abrir espaço; os demais, de tamanho fixo, nunca quebram
// linha nem são espremidos. A partir de 1024px libera o wrap pra empilhar em
// telas pequenas.
export const ContainerFiltersAndButtons = styled.div`
  width: 100%;
  height: auto;

  gap: 8px;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;

  @media (max-width: 1024px) {
    gap: 6px;
    flex-wrap: wrap;
    justify-content: center;
  }

  @media (max-width: 760px) {
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }

  @media (max-width: 479px) {
    gap: 4px;
  }
`;

// Envolve cada item de tamanho fixo (SwitchLayout, botões de filtro,
// ADICIONAR/RELATÓRIO) só pra garantir flex-shrink: 0 - eles não repassam
// className/style pro elemento raiz, então isolar aqui é o jeito de blindar
// contra o encolhimento sem alterar os componentes compartilhados. Cada um
// embrulha um item só (não agrupa vários), então o layout continua sendo uma
// única linha de itens diretos.
export const FilterFixedItem = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  // hideOnMobile: usado no RELATÓRIO - some em telas ≤760px pra dar lugar
  // aos outros filtros/ações na barra.
  @media (max-width: 760px) {
    display: ${props => props.hideOnMobile ? 'none' : 'flex'};
  }
`;

// Único item flexível da linha - ocupa exatamente "100% da linha menos o
// espaço dos outros itens" (todos de tamanho fixo): cresce quando sobra
// espaço, encolhe (sem piso) quando aperta.
// flex-basis é o "peso" que esse item usa na conta de quebra de linha do
// flex-wrap (feita com o tamanho PREFERIDO de cada item, antes de qualquer
// encolhimento) - 180px é um meio-termo calibrado pra telas ≤1024px:
// - alto o bastante pra, junto do SwitchLayout, já não sobrar espaço pro
//   ESTADOS entrar na mesma linha em telas estreitas (~375px) - senão o
//   ESTADOS "furava a fila" e espremia a busca quase a zero;
// - baixo o bastante pra nunca estourar a linha sozinho ao lado do só
//   SwitchLayout, nem em telas bem pequenas (~320px) - senão a busca cai
//   pra uma linha própria abaixo do botão de troca de layout.
// O tamanho final renderizado normalmente fica bem maior que 180px (via
// flex-grow, usando o espaço de verdade que sobrar na linha) - isso aqui só
// decide COM QUEM ela fica agrupada.
export const ContainerInput = styled.div`
  width: auto;
  height: auto;
  flex: 1 1 180px;
  min-width: 0;

  @media (max-width: 365px) {
    max-width: 241px;
  }
`;

// "Quebra-linha" forçada da barra de filtros em telas ≤479px: um item
// invisível com flex-basis: 100% sempre empurra tudo que vem depois dele pra
// uma linha nova, não importa o tamanho dos itens ao redor - truque padrão
// de flexbox pra forçar quebra num ponto exato (aqui: entre a busca e o
// ESTADOS), garantindo 2 linhas fixas (1: SwitchLayout + busca / 2: ESTADOS +
// SITUAÇÃO + ADICIONAR) em vez de depender do encaixe orgânico do flex-wrap.
// Nas telas acima de 479px fica "display: none" - some do fluxo, sem efeito.
export const FilterLineBreak = styled.div`
  display: none;
  width: 0;
  height: 0;

  @media (max-width: 479px) {
    display: block;
    flex-basis: 100%;
  }
`;

export const MetricsRow = styled.div`
  width: 100%;

  gap: 16px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 479px) {
    grid-template-columns: 1fr;
  }
`;

export const CardsGrid = styled.div`
  width: 100%;

  gap: 16px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

export const ArtCard = styled.div`
  // Sempre 3 cards por linha até a tela virar mobile (≤760px, onde vira 1 por
  // linha) - sem min-width, senão o card quebra pra 2 por linha mais cedo
  // conforme o espaço disponível encolhe (ex.: sidebar expandida). calc() em
  // vez de uma % aproximada (32.3%) pra descontar exatamente os 2 gaps de
  // 16px entre os 3 cards e não estourar a linha por arredondamento.
  width: calc((100% - 32px) / 3);
  padding: 16px;

  display: flex;
  flex-direction: column;
  gap: 8px;

  border-radius: 1rem;
  border: 1px solid ${theme.colors.gray.Cinza_11};
  background-color: ${theme.colors.white.Branco};

  @media only screen and (max-width: 760px) {
    width: 100%;
    min-width: 0;
  }
`;

export const ArtCardTopRow = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const ArtCardTitle = styled.p`
  margin: 0;
  min-width: 0;
  max-width: 100%;
  white-space: nowrap;

  // Nome que não cabe vira scroll horizontal em vez de cortar com reticências -
  // mesmo padrão de scrollbar usado nas células da tabela (TableWithLabelBlack/
  // stylesV2.js, TableRowCell), pra manter os dois consistentes.
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-gutter: stable;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    -webkit-appearance: none;
  }

  &::-webkit-scrollbar-button {
    width: 0;
    height: 0;
    display: none;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.08);
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(31, 41, 55, 0.45);
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(31, 41, 55, 0.7);
  }

  font-family: Lato;
  font-size: 15px;
  font-weight: 800;
  color: ${props => props.disabled ? theme.colors.disabled.Desabilitado_Medio : theme.colors.gray.Cinza_2};
`;

export const ArtCardHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const ArtCardCode = styled.p`
  margin: 0;

  font-family: Lato;
  font-size: 14px;
  font-weight: 800;
  color: ${props => props.disabled ? theme.colors.disabled.Desabilitado_Medio : theme.colors.gray.Cinza_2};
`;

export const ArtCardCodeRow = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ArtCardValidade = styled.p`
  margin: 0;

  font-family: Lato;
  font-size: 12px;
  color: ${props => props.disabled ? theme.colors.disabled.Desabilitado_Medio : theme.colors.gray.Cinza_6};

  b {
    font-weight: 800;
    color: ${props => props.disabled ? theme.colors.disabled.Desabilitado_Medio : theme.colors.gray.Cinza_2};
  }
`;

export const ArtCardProgressRow = styled.div`
  width: 100%;
  margin-top: 4px;

  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ArtCardProgressLabel = styled.span`
  flex-shrink: 0;
  white-space: nowrap;

  font-family: Lato;
  font-size: 11px;
  font-weight: 700;
  color: ${props => props.disabled ? theme.colors.disabled.Desabilitado_Medio : theme.colors.gray.Cinza_6};

  i {
    font-style: italic;
    font-weight: 600;
    color: ${props => props.disabled ? theme.colors.disabled.Desabilitado_Medio : theme.colors.gray.Cinza_7};
  }
`;

export const ArtCardProgressTrack = styled.div`
  flex: 1;
  min-width: 0;
  height: 6px;

  border-radius: 3.5px;
  background-color: #E4E4E8;
  overflow: hidden;
`;

export const ArtCardProgressFill = styled.div`
  width: ${props => props.percent}%;
  height: 100%;

  border-radius: 3.5px;
  background-color: ${props => props.disabled
    ? theme.colors.disabled.Desabilitado_Medio
    : props.warning
        ? theme.colors.orange.Laranja_Sudeste
        : theme.colors.green.Verde_AgroReceita};
  transition: width .3s ease;
`;

export const ProgressBar = styled.div`
  width: 120px;
  height: 7px;
  border-radius: 3.5px;
  background-color: #f3f3f5;
  padding-top: 2px;
  padding-right: ${props => props.progress};
  margin-left: 15px;

`;

export const Progress = styled.div`
  height: 3px;
  border-radius: 1.5px;
  background-color: #00b43f;

`;

export const ArtsContainer = styled.section`
  width: 100%;
  height: 100%;
  padding: 50px 0;
  max-width: ${props => props.isIndex ? '100%' : '400px'};
  
  background: #ffffff;
  margin: auto;
  
  a{
    text-decoration: none;
  }

  .search{
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-top: -80px;
    margin-bottom: 20px;
    
  }

  .filterbuttons{
    margin-left: -5px;
  }
  
  > div {
    gap: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    width: 100%;
    max-width: 1105px;
    margin: 0 auto;

    .filterbuttons {
      width: 100%;
      max-width: 400px;
      display: flex;
      justify-content: space-around;
      i{ 
        margin-top: 2px;
        margin-right: 10px;
      }
      .pesquisar {
        margin: 30px 0;
        padding: 15px 20px;
      }
    }

    #newclassbuttonfortests{
      display: flex;
    }

    .botoesfinais{
      display: flex;
      flex-direction: row;
      justify-content: space-evenly;
      width: 50%;
      margin-top: 20px;
      .loadmore {
      }
      .relatorio {

      }
    }
  }


  @media only screen and (max-width: 991px){
    form{
      width: 95%;
    }


    .search{
      .add{
        margin-left: 0px;
        a{
          text-decoration: none;
        }
      }
    }
  }


  @media only screen and (max-width: 768px) {
    background: #ffffff;
    height: 100%;

    .search{
      align-self: center;
      margin-left: 0px;
      margin-top: 65px;
      width: 100%;
      max-width: 375px;
    }

      .add{
        margin-left: -10px;
        margin-top: 10px;
        width: 100%;
        max-width: 375px;
        button{
          width: 87%;
          margin: auto;
          border-radius: 30px;
          border: solid 1px #00b43f;
          z-index: 9;
          display: flex;
          justify-content: center;
        }   
        a{
          text-decoration: none;
        }  
    }

    .filterbuttons{
      margin-left: -5px;
    }
    
    .loadmoree {
      margin-top: -50px;

        .loadmore {
          width: 100%;
          max-width: 375px;
          margin: 30px 0;
          padding: 15px 20px;

      
    }
      #newclassbuttonfortests{
        display: flex;
      }

      
      .add {
        align-self: flex-end;
        button {
          margin: 15px 0 30px;
          padding: 15px 30px;
        }
        a{
          text-decoration: none;
        }
      }
    }
    >div{

      .relatorio {
        width: 100%;
        max-width: 375px;
        margin-left: 0px;
        padding: 15px;
        justify-content: center;
      }
    }
  }

  @media (max-width: 760px) {
    padding: 24px 0;

    >div{
      max-width: 100% !important;
      padding: 0 16px 16px !important;

      margin: 0 !important;
    }
  }


  @media only screen and (max-width: 540px){
    .search{
      margin-top: 0px;
    }
  }


`;

export const ArtsAddContainer = styled.div`
  padding: 50px 0;
  background: #ffffff;
  div {
    form {
      margin: auto;
      min-width: 250px;
      max-width: 400px;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      small {
        color: #b53737;
        font-weight: 700;
      }
    }
  }
  .salvar {
    width: 100%;
    max-width: 400px;
    display: flex;
    justify-content: space-around;
    .salvado {
      margin: 30px 0;
      padding: 15px 20px;
    }
    a{
      text-decoration: none;
    }
  }
  .specific {
    display: flex;
    justify-content: space-around;
    .link{
      &:hover{
        text-decoration: none;
      }
    }

    a{
      text-decoration: none;
    }
  }

  .button-finish{
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    .btn {
      width: 100%;
    }
  }

  @media only screen and (max-width: 991px){
    div{
      form{

      }
    }
    .salvar {
      width: 100%;
      max-width: 400px;
      display: flex;
      justify-content: space-around;
      flex-direction: column-reverse;
      .salvado {
        margin: 30px 0;
        padding: 15px 20px;
        width: 100%;
      }
      a{
        text-decoration: none;
      }
    }

    .specific {
      display: flex;
      justify-content: space-around;
      flex-direction: column;
      .link{
        &:hover{
          text-decoration: none;
        }
      }

      a{
        text-decoration: none;
      }
    }
  }

  @media only screen and (max-width: 411px){
    .button-finish{
    flex-direction: column;
      .btn {
        width: 100%;
      }
    }

  }
`;

export const FormContainer = styled.div`
width: 400px;
padding: 50px 0;
background: #ffffff;
margin: auto;
form{
  margin: auto;
  min-width: 250px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  .adicionar{
    width: 400px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    .add{
      width: 146px;
      text-align: center;
      font-family: Lato;
      font-size: 11px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: normal;
      color: #589bff;
      cursor: pointer;
      position: absolute;
      margin-top: 57px;
      margin-left: 242px;
      .click{
        position: absolute;
        margin-left: 161px;
        width: 90px;
      }
    }
  }
}

 @media only screen and (max-width: 991px){
  padding: 50px 0;
  background: #ffffff;
  form{
    margin: auto;
    min-width: 250px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    .adicionar{
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: center;
      .add{
        width: 146px;
        text-align: center;
        font-family: Lato;
        font-size: 11px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: normal;
        color: #589bff;
        cursor: pointer;
        position: absolute;
        margin-top: 15px;
        margin-left: 75px;
        .click{
          position: absolute;
          margin-left: 35px;
        }
      }
        
    }
  }
 }
`;
